import { tKey, type I18nKey } from "@/core/i18n";
import { PreAnnotateModelTypeEnum } from "@repo/schema";

/**
 * Every user-facing string of the auto-annotate run panel, as `label` catalog
 * keys rendered with `t()`. The result-card wording (done / empty / error) is
 * shared with the settings-dialog banner and lives in
 * `../PreAnnotateSettingsDialog/copy`. Counted phrases (boxes drawn so far,
 * a transcript turn's boxes, a resumed conversation's turns) are plural keys
 * written out where they render, so i18next-cli sees their `count`.
 */

/**
 * "Auto-annotating, detection…".
 *
 * Deliberately does not name Robopipe Vision: naming the engine in a spinner
 * the labeler cannot act on adds nothing. The mode is what changes run to run.
 */
export const RUNNING_TITLE = {
  [PreAnnotateModelTypeEnum.SEGMENTATION]: tKey("label:runPanel.runningTitle.segmentation"),
  [PreAnnotateModelTypeEnum.DETECTION]: tKey("label:runPanel.runningTitle.detection"),
} satisfies Record<PreAnnotateModelTypeEnum, I18nKey<"label">>;

/**
 * The line under the title while a streamed Robopipe Vision turn is running.
 *
 * Each phase says the one true thing about it. `image` is over in a second and
 * would flash, so it is silent. `thinking` is the long one — two minutes on a
 * busy frame — and is normally replaced by the heading of the model's current
 * thought-summary section (see `thoughtHeadline`);
 * this is the fallback for the moment before the first one arrives, and for a
 * server running with thought streaming turned off. `boxes` counts
 * (`runPanel.streamBoxes`), because a number going up is the most convincing
 * progress there is.
 */
export const STREAM_THINKING = tKey("label:runPanel.streamThinking");

/**
 * Shown once a run passes this, to set expectations rather than to alarm.
 *
 * Robopipe Vision is bimodal on a fixed frame: the same image costs either
 * ~20 s or ~150 s and which one is not predictable. A run past a minute is
 * therefore normal, not stuck, and saying so is the difference between waiting
 * and hitting Cancel.
 */
export const SLOW_RUN_AFTER_MS = 60_000;
export const STREAM_SLOW_NOTE = tKey("label:runPanel.slowNote");

export const CANCELLED_TITLE = tKey("label:runPanel.cancelledTitle");
// A run recovered after a reload: the stream that fed the live card is gone,
// so all the card can say is that the server is still on it.
export const DETACHED_RUNNING = tKey("label:runPanel.detachedRunning");
// A recovered turn whose lock cleared without a new turn being recorded.
export const DETACHED_FAILED = tKey("label:runPanel.detachedFailed");

// ─── Robopipe Vision feedback turn ────────────────────────────────────────────

/** Body of a done card produced by a feedback turn rather than the first run. */
export const VISION_DONE_FEEDBACK_BODY = tKey("label:runPanel.visionDoneFeedbackBody");
export const BUTTON_GIVE_FEEDBACK = tKey("label:runPanel.giveFeedback");
export const BUTTON_SEND_FEEDBACK = tKey("label:runPanel.sendFeedback");
export const FEEDBACK_LABEL = tKey("label:runPanel.feedbackLabel");
export const FEEDBACK_PLACEHOLDER = tKey("label:runPanel.feedbackPlaceholder");
export const FEEDBACK_HELP = tKey("label:runPanel.feedbackHelp");

// ─── The project note a feedback turn became ─────────────────────────────────

/**
 * The model decides whether a feedback turn becomes a project note. Said on
 * the card either way, so the labeller knows whether it now steers other
 * images too, with a one-click way to override the decision: Exclude a saved
 * note, Save one that was kept out.
 */
export const NOTE_SAVED = tKey("label:runPanel.note.saved");
export const NOTE_NOT_SAVED = tKey("label:runPanel.note.notSaved");
export const BUTTON_EXCLUDE_NOTE = tKey("label:runPanel.note.exclude");
export const BUTTON_SAVE_TO_NOTES = tKey("label:runPanel.note.save");
export const NOTE_SAVE_FAILED = tKey("label:runPanel.note.saveFailed");
export const NOTE_REMOVE_FAILED = tKey("label:runPanel.note.removeFailed");
export const BUTTON_EDIT_NOTE = tKey("common:actions.edit");
export const BUTTON_SAVE_NOTE = tKey("label:actions.saveNote");
export const NOTE_EDIT_LABEL = tKey("label:runPanel.note.editLabel");
/**
 * This conversation keeps the feedback as it was sent — its history is
 * replayed every turn — so an edit only reaches the model from the next image.
 */
export const NOTE_EDIT_HELP = tKey("label:runPanel.note.editHelp");
export const NOTE_UPDATE_FAILED = tKey("label:runPanel.note.updateFailed");

export const BUTTON_CANCEL = tKey("common:actions.cancel");
/** Deliberately engine-agnostic: it runs whatever engine the project uses now. */
export const BUTTON_RUN_AGAIN = tKey("label:actions.runAgain");
/**
 * Promotes the staged boxes and saves the image, the same as the awaiting-
 * review banner's Approve: whatever the labeler kept or fixed is what they
 * approved. A plain Save keeps them staged.
 */
export const BUTTON_APPROVE = tKey("label:actions.approve");
export const BUTTON_CLOSE = tKey("common:actions.close");
export const BUTTON_SETTINGS = tKey("label:actions.autoAnnotateSettings");

// ── Resuming a conversation ──────────────────────────────────────────────────

export const TRANSCRIPT_TITLE = tKey("label:runPanel.transcript.title");
export const TRANSCRIPT_LOADING = tKey("label:runPanel.transcript.loading");
/**
 * A conversation always has at least one turn, so this is only reachable when
 * the ledger could not be read. Says so rather than implying nothing happened.
 */
export const TRANSCRIPT_UNAVAILABLE = tKey("label:runPanel.transcript.unavailable");

/**
 * What the labeler said on a turn — or, with no feedback, that this was the
 * opening detection. What came back is `runPanel.transcript.boxes`: dropped
 * boxes are the model's, not the labeler's problem.
 */
export const TRANSCRIPT_OPENING_RUN = tKey("label:runPanel.transcript.openingRun");

/** A product name: the same in every language. */
export const RESUMED_TITLE = "Robopipe Vision";
// The resumed card's body (`runPanel.resumedBody`, plural on the turn count)
// deliberately says nothing about boxes. A resumed conversation has no idea
// which of the shapes on the canvas came from it — that knowledge died with
// the page load that produced them — and claiming a count would be a guess.
